import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCipherStore, availableCiphers } from '../store/cipherStore';
import CipherLayerCard from './CipherLayerCard';
import { exportAsJSON } from '../utils';

const CipherManager = () => {
  const { layers, addLayer, clearAll } = useCipherStore();
  const [selectedCipher, setSelectedCipher] = useState('caesar');
  const [showAddForm, setShowAddForm] = useState(false);
  
  const handleAddLayer = () => {
    addLayer(selectedCipher);
    setShowAddForm(false);
  };
  
  const handleExportConfig = () => {
    const config = {
      layers: layers.map(layer => ({
        type: layer.type,
        key: layer.key,
        isActive: layer.isActive
      })),
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    exportAsJSON(config, `cipher-config-${timestamp}.json`);
  };
  
  const getCipherIcon = (type) => {
    const icons = {
      caesar: '🏛️',
      rot13: '🔄',
      base64: '📦',
      xor: '⚡',
      custom: '🎨'
    };
    return icons[type] || '🔐';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="cyber-border rounded-lg p-6 space-y-6"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-neon-purple glow-text">Cipher Layers</h2>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddForm(!showAddForm)}
            className="cyber-button rounded-lg"
          >
            ➕ Add Layer
          </motion.button>
          
          {layers.length > 0 && (
            <>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleExportConfig}
                className="cyber-button rounded-lg"
              >
                💾 Export Config
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={clearAll}
                className="cyber-button rounded-lg bg-red-900/20 border-red-500/30 text-red-400 hover:bg-red-900/30"
              >
                🗑️ Clear All
              </motion.button>
            </>
          )}
        </div>
      </div>
      
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="cyber-border rounded-lg p-4 space-y-4"
          >
            <h3 className="text-lg font-semibold text-neon-cyan">Add New Cipher Layer</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(availableCiphers).map(([key, cipher]) => (
                <motion.button
                  key={key}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedCipher(key)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedCipher === key
                      ? 'border-neon-cyan bg-neon-cyan/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{getCipherIcon(key)}</span>
                    <span className="font-semibold">{cipher.name}</span>
                  </div>
                  <p className="text-xs text-gray-400">{cipher.description}</p>
                  {cipher.requiresKey && (
                    <p className="text-xs text-neon-cyan mt-1">
                      Requires: {cipher.keyLabel}
                    </p>
                  )}
                </motion.button>
              ))}
            </div>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleAddLayer}
                className="cyber-button rounded-lg"
              >
                ✓ Add {availableCiphers[selectedCipher].name}
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddForm(false)}
                className="cyber-button rounded-lg"
              >
                ✕ Cancel
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="space-y-4">
        <AnimatePresence>
          {layers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 text-gray-500"
            >
              <div className="text-6xl mb-4">🔐</div>
              <p className="text-lg mb-2">No cipher layers added yet</p>
              <p className="text-sm">Add a cipher layer to start encrypting your text</p>
            </motion.div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>Processing order:</span>
                <div className="flex items-center gap-1">
                  {layers.map((layer, index) => (
                    <React.Fragment key={layer.id}>
                      {index > 0 && <span>→</span>}
                      <span className={`px-2 py-1 rounded text-xs ${
                        layer.isActive ? 'bg-neon-cyan/20 text-neon-cyan' : 'bg-gray-700 text-gray-400'
                      }`}>
                        {layer.cipher.name}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
              
              {layers.map((layer, index) => (
                <CipherLayerCard
                  key={layer.id}
                  layer={layer}
                  index={index}
                />
              ))}
            </>
          )}
        </AnimatePresence>
      </div>
      
      {layers.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xs text-gray-500 bg-gray-800/30 p-3 rounded-lg"
        >
          <p className="mb-1">
            <strong>Encryption:</strong> Layers are applied in order (top to bottom)
          </p>
          <p>
            <strong>Decryption:</strong> Layers are applied in reverse order (bottom to top)
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CipherManager;
